import styles from "../../../../MapMonitoring/components/AddMarkerModal/AddIncidentModal.module.css";
import React, { useEffect, useMemo, useReducer } from "react";
import { TextField } from "@mui/material";
import useAppSnackbar from "../../../../../hooks/useAppSnackbar";

import { companyService } from "../../../../../services/guide/company-service";
import { CreateCompany, UpdateCompany, Company } from "../../../../../types/api/guide/company.types";

type FormState = { name: string };

type Action =
    | { type: "SET_FIELD"; name: keyof FormState; value: any }
    | { type: "SET_All"; payload: FormState }
    | { type: "RESET" };

const initialState: FormState = { name: "" };

const reducer = (state: FormState, action: Action): FormState => {
    switch (action.type) {
        case "SET_FIELD":
            return { ...state, [action.name]: action.value };
        case "SET_All":
            return { ...action.payload };
        case "RESET":
            return initialState;
        default:
            return state;
    }
};

interface CompanyAddModalProps {
    onClose: () => void;
    refetchData: () => void;
    company?: Company;
}

export const CompanyAddModal = ({ onClose, refetchData, company }: CompanyAddModalProps) => {
    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();
    const isEdit = Boolean(company?.id);

    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        if (!company) {
            dispatch({ type: "RESET" });
            return;
        }

        dispatch({
            type: "SET_All",
            payload: {
                name: company.name ?? "",
            },
        });
    }, [company]);

    const handleValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        dispatch({ type: "SET_FIELD", name: name as keyof FormState, value });
    };

    const canSave = useMemo(() => !!state.name.trim(), [state.name]);

    const handleSave = async () => {
        try {
            const name = state.name.trim();

            if (!isEdit) {
                const payload: CreateCompany = { name };
                await companyService.create(payload);
                showSuccess("Организация создана");
            } else {
                const id = company!.id;
                const payload: UpdateCompany = { id, name };
                await companyService.update(id, payload);
                showSuccess("Организация обновлена");
            }

            refetchData();
            onClose();
            dispatch({ type: "RESET" });
        } catch (e: any) {
            showError(e?.response?.data ?? "Ошибка при обновлении/добавлении организации");
        }
    };

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        {isEdit ? "Редактировать организацию" : "Добавить организацию"}
                    </h3>
                    <button className={styles.modalClose} onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.modalField}>
                        <div className={styles.modalLabel}>Название</div>
                        <div className={styles.modalCoords}>
                            <TextField name="name" value={state.name} onChange={handleValue} fullWidth />
                        </div>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.modalButton} onClick={onClose}>
                        Отмена
                    </button>

                    <button
                        className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
                        onClick={handleSave}
                        disabled={!canSave}
                        style={{ opacity: canSave ? 1 : 0.6 }}
                    >
                        Сохранить
                    </button>
                </div>
            </div>

            <SnackbarComponent />
        </div>
    );
};
